import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Chrome from '@uiw/react-color-chrome';
import PropTypes from 'prop-types';

import Prefix from '@asteria/component-prefix';

import { useTranslate } from '@asteria/language';
import { cn } from '@asteria/utils-funcs/classes';

import Dropdown, { DropdownItem } from '../dropdown';

import './index.scss';

const ColorBox = (props) => {
	const { color, onClick } = props;

	const style = useMemo(() => ({ backgroundColor: color }), [color]);

	const handleClick = useCallback(() => {
		return onClick?.(color);
	}, [color, onClick]);

	return (
		<div
			className="w-6 h-6 rounded-sm"
			key={`color-${color}`}
			style={style}
			onClick={handleClick}
		/>
	);
};

ColorBox.propTypes = {
	color: PropTypes.string,
	onClick: PropTypes.func,
};

const ColorPicker = (props) => {
	const { selectedColor, colors, onSelect, className, tooltip } = props;

	const [color, setColor] = useState('#FFF');

	const { t } = useTranslate();

	useEffect(() => {
		if (selectedColor) {
			setColor(selectedColor);
		} else {
			setColor(colors?.[0]);
		}
	}, [colors, selectedColor]);

	const handleSetColor = useCallback(
		(color) => {
			const newColor = typeof color === 'object' ? color?.hexa : color;

			setColor?.(newColor);
			onSelect?.(newColor);

			return null;
		},
		[onSelect],
	);

	const toggle = useMemo(
		() => ({
			children: (
				<Prefix
					colors={color}
					className="asteria-component__color-picker__prefix"
				/>
			),
			icon: 'triangle-down',
			iconActive: 'triangle-up',
			iconPosition: 'last',
			tooltip: tooltip,
		}),
		[color, tooltip],
	);

	const colorSelectorStyle = useMemo(
		() => ({
			boxShadow: 'none',
			border: 0,
			padding: '16px',
			paddingBottom: '0px',
			width: '250px',
			'--github-arrow-border-color': 'transparent',
		}),
		[],
	);

	return (
		<Dropdown
			placement="bottom-end"
			direction="row"
			toggle={toggle}
			className={cn('asteria-component__color-picker', className)}
			title={t(['color.picker.title', 'color.picker.dropdown.title'])}
			analyticsKey="colorPicker"
			scroll
		>
			<DropdownItem noColors stopPropagation className="p-0">
				<div
					className={cn(
						'grid flex-col w-64',
						'asteria-component__color-picker-item',
					)}
				>
					<Chrome
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						style={colorSelectorStyle}
						showColorPreview={false}
						color={color}
						onChange={handleSetColor}
					/>

					<div className="flex flex-row gap-2 justify-center pb-4">
						{[color, ...colors]?.map((color, index) => (
							<ColorBox
								key={`color-${color}-${index}`}
								color={color}
								onClick={handleSetColor}
							/>
						))}
					</div>
				</div>
			</DropdownItem>
		</Dropdown>
	);
};

ColorPicker.propTypes = {
	selectedColor: PropTypes.string,
	className: PropTypes.string,
	colors: PropTypes.array,
	onSelect: PropTypes.func,
	onAction: PropTypes.func,
	tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export default ColorPicker;
